import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import AudioPlayerBar, {
  type AudioPlayerBarTrack,
} from "../../components/audio-player-bar/AudioPlayerBar";
import MediaPreview from "../../components/media-preview/MediaPreview";
import QuoteCard from "../../components/quote-card/QuoteCard";
import SvgLoadingAnimation from "../../components/loading";
import {
  useGetGalleryWorkTagsQuery,
  useGetGalleryWorksQuery,
  type GalleryCreator,
  type GalleryWork,
} from "../../app/services/gallery";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import {
  hashString,
  mulberry32,
} from "../../utils/cloud-layout/commentCloudLayout";
import type { DeviceState } from "../../types/media-preview";
import ScaleThroughStage, {
  type ScaleThroughStageItem,
  type StageExclusionRect,
} from "./components/ScaleThroughStage";

/**
 * GALLERY ページ。
 *
 * プロフィール作品に設定されたハッシュタグ（GALLERY 検索用メタデータ）と
 * 作品名で、公開中プロフィールの作品を横断検索する。
 *
 * - 検索フォームは画面中央に配置（入力に応じてタグ候補をサジェスト、
 *   未入力時は人気タグ）。Getty "Tracing Art" の初期画面と同じ構図。
 *   PC ではビューポート中央に固定（fixed）し、スクロールやグローバル
 *   ヘッダーの出し入れ（padding-top 切替による全体のずれ）でも動かない。
 * - フリーワード検索（タグ完全一致 + 作品名部分一致 + アーティスト名・
 *   肩書き部分一致の和集合）は入力に追従するライブ検索。デバウンス後に
 *   自動で検索し、Enter で即時確定もできる。空にすれば検索解除。
 * - タグチップの選択はタグ完全一致検索。フリーワードとは排他で、
 *   チップ選択後に入力を変えるとフリーワード検索へ戻る。
 * - アーティスト名・肩書きにヒットしたクリエイターは、公開プロフィールの
 *   ステートメントセクション（ProfileStatement）をフォントサイズだけ
 *   コンパクトにした完全白パネル（アバター + 引用アイコン付き本文 →
 *   右下に引用線 + 名前 + 肩書き）として作品カードに混在表示する。幅は
 *   作品チップより広め（レイアウト幅の 30%、320〜420px）を確保して
 *   大きく見せ、高さはステートメントの折返し行数（最大 5 行）に追従する。
 *   ステージでは専用のパネルスロットでアーティストだけを巡回させる。
 *   クリックで公開プロフィール `/{slug}`（RootSegmentDispatcher）へ
 *   遷移する。
 * - タグ候補はハッシュタグ（= `ContentTag` の領分）なので、青の `FilterChip` とは
 *   区別して light-gray のチップで表示する（クリックで選択できる点だけ独自）
 * - 検索結果は PC・モバイル共通で「画面固定のスケールスルー表示」
 *   （`ScaleThroughStage`）。ページはスクロールせず、wheel / スワイプの
 *   仮想スクロール量に応じて、作品が画面中心に対する同心円配置（検索 UI の
 *   実測矩形と要素同士の重なりを避けたセルパッキング）で奥（小さく・
 *   ボケて・透明）から現れ、鮮明になりながら手前へ拡大し、画面を突き抜けて
 *   フェードアウト + ブラーで消える（参考:
 *   https://motion-demos-psi.vercel.app/scale-through-scroll/ ）。
 *   スロットは周回ごとに次の作品へ再利用され、全件を順番に無限ループで
 *   流すため、件数が多くても取りこぼしなく表示できる。同一の作品・
 *   アーティストが 1 画面に同時に出ることはない。
 * - クリエイターへの導線は音源再生バー（`AudioPlayerBar`）側に持たせるため、
 *   作品カードにはアバター・ユーザー名を表示しない。
 */

/** 検索条件。タグチップ選択（tag 完全一致）とフリーワード（q）で排他。 */
type GallerySearch = { kind: "tag" | "q"; value: string };

/** ステージへ投入する要素。アーティストパネルと作品カードを混在させる。 */
type GalleryStageEntry =
  | { kind: "creator"; creator: GalleryCreator }
  | { kind: "work"; work: GalleryWork };

/** ステージアイテムのキー（作品 id と衝突しないよう creator は接頭辞付き）。 */
const stageEntryKey = (entry: GalleryStageEntry): string =>
  entry.kind === "creator"
    ? `creator:${entry.creator.slug}`
    : String(entry.work.id);

// ===== Styled =====

/** PageContainer の左右 padding(px)。レイアウト幅（layoutWidth）の基準。 */
const PAGE_PAD_X = 16;

/** 検索バー（SearchField）の高さの半分(px)。padding 10×2 + 入力 24 +
 * border 1×2 = 46px の半分。fixed ヒーローで検索バーの中心を
 * ビューポート中央にアンカーするための見積もり。 */
const SEARCH_BAR_HALF_H_PX = 23;

const PageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  padding: 0 ${PAGE_PAD_X}px 48px;
`;

/**
 * 検索 UI をビューポート中央に浮かせるヒーロー。ステージ（fixed, z: 0）の
 * 前面（z: 1）に重なるため、ラッパーはクリックを透過し、子要素だけ
 * pointer-events を戻す。ステージのアイテムは検索 UI の背面を通過する。
 *
 * PC（$fixedCenter）はビューポート基準の position: fixed で固定する。
 * グローバルヘッダーの出し入れ（ContentWrapper の padding-top 64px ⇔ 0
 * 切替）でも検索バーは動かない。コア全体の上下中央寄せではなく
 * 「検索バー（コア先頭）の中心 = ビューポート中央」に上端をアンカーする
 * ことで、タグ候補・ローディングの増減でコアの高さが変わっても
 * 下方向へ伸びるだけになり、検索バー自体は動かない。
 * モバイルは通常フロー（画面上部に置き、ステージが背面に広がる）。
 */
const Hero = styled.section<{ $fixedCenter: boolean; $centered: boolean }>`
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;

  ${({ $fixedCenter, $centered }) =>
    $fixedCenter
      ? css`
          position: fixed;
          inset: 0;
          justify-content: flex-start;
          padding: calc(50vh - ${SEARCH_BAR_HALF_H_PX}px) ${PAGE_PAD_X}px 0;
        `
      : css`
          position: relative;
          width: 100%;
          justify-content: ${$centered ? "center" : "flex-start"};
          min-height: ${$centered ? "calc(100vh - 160px)" : "auto"};
          padding: ${$centered ? "24px 0" : "48px 0 8px"};
        `}

  & > * {
    pointer-events: auto;
  }
`;

/** 検索 UI（検索バー + タグクラウド）の縦積みラッパー。可視子要素の
 * 合成矩形を実測し、ステージが避ける除外矩形として渡す。 */
const CenterCore = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

/** TopAppBarHome の検索フィールドと同じ視覚言語（枠 + 薄影 + 検索アイコン）。 */
const SearchField = styled.div`
  width: 100%;
  max-width: 600px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fff;
  box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.05);

  &:focus-within {
    border-color: var(--soremo-blue);
    box-shadow: 2px 4px 8px 3px rgba(0, 154, 206, 0.1);
  }
`;

const SearchIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 20px;
  color: var(--soremo-light-gray);
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  /* type 属性を付けず soremo_style.scss の input[type="text"] を回避する。 */
  width: 100%;
  min-height: 24px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  outline: none;
  font-size: 16px;
  line-height: 1.5;

  &::placeholder {
    color: var(--soremo-placeholder);
  }
`;

const TagCloud = styled.div`
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

/**
 * 検索候補のハッシュタグチップ。`ContentTag` と同じ light-gray の視覚言語
 * （pill + border + background 地）で、`FilterChip`（青 = グループ分け）とは
 * 色で役割を区別する。表示専用の `ContentTag` と違いクリックで選択できる。
 */
const HashTagChip = styled.button.attrs({
  className: "bodytext-13",
})<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 4px 12px;
  white-space: nowrap;
  line-height: 1;
  cursor: pointer;
  appearance: none;
  margin: 0;
  transition: background-color 0.15s, color 0.15s, border-color 0.15s;

  ${({ $selected }) =>
    $selected
      ? `
  color: #fff;
  background-color: var(--soremo-light-gray);
  border: 1px solid var(--soremo-light-gray);
  `
      : `
  color: var(--soremo-light-gray);
  background-color: var(--soremo-background);
  border: 1px solid var(--soremo-border);

  &:hover {
    color: var(--soremo-gray);
    border-color: var(--soremo-light-gray);
  }
  `}

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
  }
`;

/**
 * チップのサムネ枠。採寸と同じ寸法を明示し、実メディアの縦横比が
 * 見積もりと違っても枠からはみ出さない（中央寄せ + クロップで吸収）。
 */
const StageThumbBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

/**
 * サムネ画像の読み込み完了までのプレースホルダー。音源サムネの黒背景や
 * 動画ポスターの読み込み前の黒画面が拡大されて目立つのを避けるため、
 * 画像の decode が終わるまでは背景色のソフトな枠だけを見せる。
 */
const ThumbLoadingBox = styled.div`
  border-radius: 6px;
  background: var(--soremo-background);
  animation: gallery-thumb-pulse 1.2s ease-in-out infinite alternate;

  @keyframes gallery-thumb-pulse {
    from {
      opacity: 0.55;
    }
    to {
      opacity: 1;
    }
  }
`;

/** 作品チップのタイトル（1 行省略）。 */
const StageTitle = styled.span.attrs({ className: "bodytext-13" })`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

// ===== ステージ採寸（scale = 1 時点の基準サイズ） =====

const clamp = (v: number, min: number, max: number): number =>
  Math.min(Math.max(v, min), Math.max(min, max));

/** チップ基準サイズ(px)。ビューポート短辺に応じてスケールする。
 * 同時表示数をビューポート面積に応じて増やしたぶん、基準サイズは
 * 以前（0.28 / 200px）よりひと回り絞って密度と可読性を両立させる。 */
const BASE_SIDE_MIN_PX = 132;
const BASE_SIDE_MAX_PX = 176;
const BASE_SIDE_VIEWPORT_RATIO = 0.21;
/** モバイルの基準サイズ縮小率（参考デモの ≤720px と同じ 0.82）。 */
const MOBILE_BASE_SIDE_FACTOR = 0.82;
/** id ハッシュ由来のサイズゆらぎ（±38%）。ギャラリーらしいメリハリを出す。 */
const SIZE_JITTER = 0.38;
/**
 * サムネイル幅のクランプ(px)。ステージの最終スケールは等倍のため、
 * この値がそのまま画面上の最大サイズになる。
 */
const THUMB_WIDTH_MIN_PX = 84;
const THUMB_WIDTH_MAX_PX = 260;
/**
 * サムネイル高さの上限(px)。縦長作品が 1 件でもあると、その高さが
 * ステージのセル寸法（種別内の最大実寸）を押し広げて配置帯が痩せるため、
 * 高さを上限で抑えて縦横比を保ったまま幅ごと縮める。
 */
const THUMB_HEIGHT_MAX_PX = 240;
/** アスペクト比のクランプ（縦長 3:5 〜 横長 16:9 強）。 */
const THUMB_AR_MIN = 0.6;
const THUMB_AR_MAX = 1.9;
/** アーティストパネル幅のクランプ(px)（scale = 1 時点の基準値）。
 * 最大幅は同心円配置の左右方向に余地を残すため 420px に絞る。 */
const CREATOR_PANEL_W_MIN_PX = 320;
const CREATOR_PANEL_W_MAX_PX = 420;
/** アーティストパネル幅のレイアウト幅比（PC）。 */
const CREATOR_PANEL_WIDTH_RATIO = 0.3;
/** アーティストパネル幅のレイアウト幅比（モバイル）。 */
const CREATOR_PANEL_WIDTH_RATIO_MOBILE = 0.86;
/** パネルの padding / 行間 / 署名行の高さ見積もり(px)。
 * ステージ採寸のためにここで再現しているので、`QuoteCard` の実スタイルと
 * 一致させること。 */
const CREATOR_PANEL_PAD_Y_PX = 16;
const CREATOR_PANEL_PAD_X_PX = 20;
const CREATOR_PANEL_GAP_PX = 12;
const CREATOR_FOOTER_H_PX = 24;
/** アーティストパネルのアバター径(px)。ProfileStatement の PC 144 /
 * モバイル 48 に対するコンパクト版。 */
const CREATOR_AVATAR_SIZE_PX = 64;
const CREATOR_AVATAR_SIZE_MOBILE_PX = 48;
/** `QuoteCard` の本文行の gap(px)。本文行幅の算出に使う
 * （アバターの wide 影の重なり回避で広め）。 */
const CREATOR_BODY_GAP_PX = 32;
/** 本文上の余白（u-mt16。引用アイコンとの重なり回避）(px)。 */
const CREATOR_STATEMENT_MT_PX = 16;
/** ステートメント本文の行高（bodytext-13 = 13px × 200%）(px)。 */
const CREATOR_STATEMENT_LINE_H_PX = 26;
/** ステートメントの最大表示行数。 */
const CREATOR_STATEMENT_MAX_LINES = 5;
/** 折返し見積もり用の字幅(px)。13px + letter-spacing 2.5px（半角は約半分）。 */
const CREATOR_STATEMENT_CHAR_W_FULL_PX = 15.5;
const CREATOR_STATEMENT_CHAR_W_HALF_PX = 9.5;
/** サムネイル読み込み（実アスペクト比の確定）を待つ上限(ms)。 */
const RATIO_SETTLE_TIMEOUT_MS = 1200;
/** 作品チップのタイトル行の高さ(px)。bodytext-13 = 13px × 200%。
 * ステージの衝突判定用の実寸高さへ加算する。 */
const STAGE_TITLE_H_PX = 26;
/** ステージ除外矩形（検索 UI 実測値）の量子化(px)。微小な寸法変化での
 * 再パッキング（飛行中の座標ジャンプ）を抑える。粗くしすぎると除外矩形が
 * 実際の UI より大きく膨らみ、そのぶん配置帯が痩せる。 */
const EXCLUSION_QUANT_PX = 16;

/** メディア種別ごとのアスペクト比フォールバック（画像は実測が優先）。 */
const fallbackAspectRatio = (work: GalleryWork): number => {
  const type = work.media[0]?.type;
  if (type === "audio") return work.image_url ? 4 / 3 : 1;
  if (type === "video") return 16 / 9;
  if (type === "pdf") return 3 / 4;
  if (!work.media[0] && work.youtube_link) return 16 / 9;
  return 4 / 3;
};

/**
 * 作品サムネの画像ソース（サムネイル画像 or 先頭の画像メディア）。
 * 事前読み込み（実比率の取得 + decode 済み判定）と表示ゲートで共用する。
 */
const workThumbImageSrc = (work: GalleryWork): string | null =>
  work.image_url ||
  (work.media[0]?.type === "image" ? work.media[0].url : null);

/**
 * 作品ごとのサムネ表示サイズ。id ハッシュのゆらぎで面積を散らし、
 * アスペクト比 ar で幅と高さへ配分する（HOME の待機雲と同じ流儀）。
 */
const workThumbSize = (
  workId: string,
  ar: number,
  baseSide: number,
): { width: number; height: number } => {
  const jitter =
    1 - SIZE_JITTER + mulberry32(hashString(`gallery:${workId}`))() * SIZE_JITTER * 2;
  const side = baseSide * jitter;
  const clampedAr = clamp(ar, THUMB_AR_MIN, THUMB_AR_MAX);
  let width = Math.round(
    clamp(side * Math.sqrt(clampedAr), THUMB_WIDTH_MIN_PX, THUMB_WIDTH_MAX_PX),
  );
  let height = Math.round(width / clampedAr);
  // 縦長すぎるサムネは、比率を保ったまま高さ上限に収める。
  if (height > THUMB_HEIGHT_MAX_PX) {
    height = THUMB_HEIGHT_MAX_PX;
    width = Math.max(THUMB_WIDTH_MIN_PX, Math.round(height * clampedAr));
  }
  return { width, height };
};

/** パネル内でステートメント本文が使える行幅(px)。アバター径はモバイル
 * （48px）でも PC の 64px で見積もる。行幅を実際より狭く見積もるぶん
 * 行数・パネル高さが過大側に振れ、はみ出さない安全側の近似になる。 */
const creatorStatementLineWidth = (panelWidth: number): number =>
  panelWidth -
  CREATOR_PANEL_PAD_X_PX * 2 -
  CREATOR_AVATAR_SIZE_PX -
  CREATOR_BODY_GAP_PX;

/**
 * ステートメントの折返し行数の見積もり（1〜最大 5 行）。全角 / 半角の
 * 目安字幅で総幅を積算して行幅で割る。クランプ行数（描画側）と高さ見積もり
 * （配置 spec）の両方で使い、同じ行数を共有する。
 *
 * `QuoteCard` は本文を `white-space: pre-wrap` で描画する（本家
 * `ProfileStatement` と同じく改行位置を尊重する）ため、明示的な改行は必ず
 * 行を分ける。段落ごとに折返し行数を求めて合算する。
 */
const estimateStatementLines = (
  statement: string,
  lineWidthPx: number,
): number => {
  if (!statement.trim() || lineWidthPx <= 0) return 0;
  let lines = 0;
  for (const paragraph of statement.split("\n")) {
    let total = 0;
    for (const ch of paragraph) {
      total +=
        ch.charCodeAt(0) > 0x7f
          ? CREATOR_STATEMENT_CHAR_W_FULL_PX
          : CREATOR_STATEMENT_CHAR_W_HALF_PX;
    }
    // 空行も 1 行ぶんの高さを占める。
    lines += Math.max(1, Math.ceil(total / lineWidthPx));
  }
  return clamp(lines, 1, CREATOR_STATEMENT_MAX_LINES);
};

/** アーティストパネルの高さ(px)。ステートメント行数（最大 5 行）に追従する。 */
const creatorPanelHeight = (statement: string, panelWidth: number): number => {
  const lines = estimateStatementLines(
    statement,
    creatorStatementLineWidth(panelWidth),
  );
  const bodyHeight = Math.max(
    CREATOR_AVATAR_SIZE_PX,
    lines > 0
      ? CREATOR_STATEMENT_MT_PX + lines * CREATOR_STATEMENT_LINE_H_PX
      : 0,
  );
  return (
    CREATOR_PANEL_PAD_Y_PX * 2 +
    bodyHeight +
    CREATOR_PANEL_GAP_PX +
    CREATOR_FOOTER_H_PX
  );
};

// ===== 本体 =====

const Gallery: React.FC = () => {
  const { isMobile } = useSelector(
    (state: { device: DeviceState }) => state.device,
  );
  const navigate = useNavigate();

  // 検索条件 / 再生中作品は URL クエリ（?tag=… or ?q=…、&work=…）と同期し、
  // 共有リンクやリロードから同じ状態を復元できるようにする。
  // GALLERY の作品は検索経由でしか取得できないため、作品単体ではなく
  // 検索条件 + 作品 ID の組をディープリンクとして扱う。
  const [searchParams, setSearchParams] = useSearchParams();
  // ディープリンク（?q=…）からの復元時は入力欄にも同じ文言を出す。
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("q") ?? "",
  );
  const [search, setSearch] = useState<GallerySearch | null>(() => {
    const tag = searchParams.get("tag");
    if (tag) return { kind: "tag", value: tag };
    const q = searchParams.get("q");
    if (q) return { kind: "q", value: q };
    return null;
  });
  const [playingId, setPlayingId] = useState<string | null>(
    () => searchParams.get("work"),
  );
  // 共有リンク（?work=）で開いた回かどうか。再生に合わせて URL の work は
  // 書き換わるので、マウント時点の値を控えておく。共有された人にはまず
  // 鑑賞モードで作品として観てもらう。
  const openedFromShareLink = useRef(searchParams.get("work") != null);

  // タグチップの選択状態表示用（フリーワード検索中は null）。
  const activeTag = search?.kind === "tag" ? search.value : null;

  useEffect(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete("tag");
        next.delete("q");
        if (search) next.set(search.kind, search.value);
        if (playingId) next.set("work", playingId);
        else next.delete("work");
        return next;
      },
      { replace: true },
    );
  }, [search, playingId, setSearchParams]);

  const debouncedInput = useDebouncedValue(searchInput.trim(), 250);

  // 「確定済み」とみなす直近の入力値。タグチップ選択・Enter・ディープ
  // リンク復元の時点の入力値を控え、入力が実際に変わるまでライブ検索が
  // 検索条件を上書きしないようにする（チップ選択直後に入力残りの
  // デバウンスが発火してタグ検索が q 検索へ化けるのを防ぐ）。
  const committedInputRef = useRef(searchInput.trim());

  // ライブ検索: 入力の変化（デバウンス済み）に追従してフリーワード検索
  // する。Enter を待たないので、作品名・アーティスト名・タグ名を打つ
  // だけで結果が現れる。空にすれば検索解除（初期のヒーロー表示に戻る）。
  useEffect(() => {
    if (debouncedInput === committedInputRef.current) return;
    committedInputRef.current = debouncedInput;
    const keyword = debouncedInput.replace(/^#+/, "").trim();
    setSearch(keyword ? { kind: "q", value: keyword } : null);
  }, [debouncedInput]);

  // タグ候補（未入力時は使用数上位 = 人気タグ）。
  const { data: tagsResp } = useGetGalleryWorkTagsQuery({
    q: debouncedInput || undefined,
  });
  const tagSuggestions = tagsResp?.data?.tags ?? [];

  // 検索条件（タグ完全一致 or フリーワード）に一致する作品一覧。
  const { data: worksResp, isFetching: isFetchingWorks } = useGetGalleryWorksQuery(
    search?.kind === "tag" ? { tag: search.value } : { q: search?.value ?? "" },
    { skip: !search },
  );
  const works = useMemo(
    () => (search ? worksResp?.data?.works ?? [] : []),
    [search, worksResp],
  );
  // アーティスト名・肩書きヒットのクリエイター（肩書きタグの完全一致は
  // 作品を含めずアーティストカードのみ返る）。
  const creators = useMemo(
    () => (search ? worksResp?.data?.creators ?? [] : []),
    [search, worksResp],
  );

  // ステージ採寸用のコンテナ幅とビューポート高さ（リサイズ追従）。
  const containerRef = useRef<HTMLDivElement>(null);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(
    () => window.innerHeight,
  );
  useEffect(() => {
    const measure = () => {
      const el = containerRef.current;
      if (el) setLayoutWidth(el.clientWidth - PAGE_PAD_X * 2);
      setViewportHeight(window.innerHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // 検索 UI（CenterCore の可視子要素）の合成矩形。ステージが避ける除外
  // 矩形としてビューポート座標で渡す。量子化して参照を安定させ、タグ候補の
  // 行数変化などによる再パッキング（飛行中の座標ジャンプ）を抑える。
  const centerCoreRef = useRef<HTMLDivElement>(null);
  const [exclusionRect, setExclusionRect] = useState<StageExclusionRect | null>(
    null,
  );
  const measureExclusion = useCallback(() => {
    const el = centerCoreRef.current;
    if (!el) return;
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;
    Array.from(el.children).forEach((child) => {
      const rect = child.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      left = Math.min(left, rect.left);
      top = Math.min(top, rect.top);
      right = Math.max(right, rect.right);
      bottom = Math.max(bottom, rect.bottom);
    });
    if (!Number.isFinite(left)) {
      setExclusionRect(null);
      return;
    }
    const qLeft = Math.floor(left / EXCLUSION_QUANT_PX) * EXCLUSION_QUANT_PX;
    const qTop = Math.floor(top / EXCLUSION_QUANT_PX) * EXCLUSION_QUANT_PX;
    const qWidth =
      Math.ceil(right / EXCLUSION_QUANT_PX) * EXCLUSION_QUANT_PX - qLeft;
    const qHeight =
      Math.ceil(bottom / EXCLUSION_QUANT_PX) * EXCLUSION_QUANT_PX - qTop;
    setExclusionRect((prev) =>
      prev &&
      prev.left === qLeft &&
      prev.top === qTop &&
      prev.width === qWidth &&
      prev.height === qHeight
        ? prev
        : { left: qLeft, top: qTop, width: qWidth, height: qHeight },
    );
  }, []);
  useEffect(() => {
    const el = centerCoreRef.current;
    if (!el) return undefined;
    measureExclusion();
    const observer = new ResizeObserver(measureExclusion);
    observer.observe(el);
    // Hero（親）の寸法変化も観測する。モバイルでは検索前後で中央寄せ ⇔
    // 上部フローが切り替わり、CenterCore 自体のサイズが変わらなくても
    // 位置が動くため。
    if (el.parentElement) observer.observe(el.parentElement);
    window.addEventListener("resize", measureExclusion);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measureExclusion);
    };
  }, [measureExclusion]);
  // ResizeObserver はサイズ変化しか拾わないため、レイアウトモードの切替
  //（モバイルの中央寄せ ⇔ 上部フロー、結果の有無）でも明示的に再実測する。
  useEffect(() => {
    measureExclusion();
  }, [measureExclusion, isMobile, search, isFetchingWorks]);

  // ステージへ投入する要素。アーティストパネルを先頭に置き、最初の
  // スロット再利用から順に登場させる。
  const stageEntries = useMemo<GalleryStageEntry[]>(
    () => [
      ...creators.map((creator) => ({ kind: "creator" as const, creator })),
      ...works.map((work) => ({ kind: "work" as const, work })),
    ],
    [creators, works],
  );

  // ── サムネイルの実アスペクト比と decode 済み状態を事前読み込みで取る ──
  // チップ採寸の精度を上げるため、画像サムネ（image_url / 画像メディア）の
  // 実比率を取り、全件確定（またはタイムアウト）してからステージを表示する
  // （HOME の待機雲と同じ流儀）。飛行中にチップ寸法が変わるポップを防ぐ。
  // あわせて id ごとの読み込み結果（loaded / error）を記録し、読み込みが
  // 終わるまでは実サムネの代わりにプレースホルダーを出す（音源サムネの
  // 黒背景などが拡大されて目立つのを防ぐ）。
  const [thumbRatios, setThumbRatios] = useState<ReadonlyMap<string, number>>(
    () => new Map(),
  );
  const [thumbStatus, setThumbStatus] = useState<
    ReadonlyMap<string, "loaded" | "error">
  >(() => new Map());
  const [ratiosSettled, setRatiosSettled] = useState(false);
  useEffect(() => {
    const targets = works.flatMap((work) => {
      const src = workThumbImageSrc(work);
      return src ? [{ id: String(work.id), src }] : [];
    });
    if (targets.length === 0) {
      setRatiosSettled(true);
      return undefined;
    }
    let cancelled = false;
    let pending = targets.length;
    setRatiosSettled(false);
    const settle = () => {
      pending -= 1;
      if (pending <= 0 && !cancelled) setRatiosSettled(true);
    };
    const images = targets.map((target) => {
      const img = new Image();
      img.onload = () => {
        if (!cancelled) {
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            const ratio = img.naturalWidth / img.naturalHeight;
            setThumbRatios((prev) => new Map(prev).set(target.id, ratio));
          }
          setThumbStatus((prev) => new Map(prev).set(target.id, "loaded"));
        }
        settle();
      };
      img.onerror = () => {
        if (!cancelled) {
          setThumbStatus((prev) => new Map(prev).set(target.id, "error"));
        }
        settle();
      };
      img.src = target.src;
      return img;
    });
    const timeout = window.setTimeout(() => {
      if (!cancelled) setRatiosSettled(true);
    }, RATIO_SETTLE_TIMEOUT_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      images.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [works]);

  // 共有リンク: 現在の GALLERY URL に検索条件（tag / q）+ work を載せた
  // ディープリンク。環境差を吸収するため現在 URL から組み立てる。
  const buildShareUrl = useCallback(
    (workId: string) => {
      const url = new URL(window.location.href);
      url.search = "";
      if (search) url.searchParams.set(search.kind, search.value);
      url.searchParams.set("work", workId);
      return url.toString();
    },
    [search],
  );

  // フローティングプレイヤーの再生キュー（検索結果の音源作品のみ、表示順）。
  const audioTracks = useMemo<AudioPlayerBarTrack[]>(
    () =>
      works
        .filter((w) => w.media[0]?.type === "audio")
        .map((w) => ({
          id: String(w.id),
          title: w.title,
          src: w.media[0].url,
          thumbnailSrc: w.image_url || undefined,
          waveformUrl: w.media[0].waveform_url || undefined,
          creatorName: w.creator.display_name,
          creatorSlug: w.creator.slug,
          shareUrl: buildShareUrl(String(w.id)),
          subtitleText: w.subtitle_text || undefined,
        })),
    [works, buildShareUrl],
  );

  // タグ変更等でキューから外れたらバーを閉じる（index は -1 になる）。
  const playingIndex = playingId ? audioTracks.findIndex((t) => t.id === playingId) : -1;

  const handleSelectTag = (name: string) => {
    // 入力欄に文言が残っていても、チップ選択をライブ検索で上書きしない。
    committedInputRef.current = searchInput.trim();
    setSearch((cur) =>
      cur?.kind === "tag" && cur.value === name
        ? null
        : { kind: "tag", value: name },
    );
  };

  // Enter はデバウンスを待たずにフリーワード検索を即時確定する。
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      committedInputRef.current = searchInput.trim();
      const keyword = searchInput.trim().replace(/^#+/, "").trim();
      setSearch(keyword ? { kind: "q", value: keyword } : null);
    }
  };

  // ===== 作品カードのサムネ（ProfileMediaSection と同じ流儀） =====
  const renderThumb = useCallback((work: GalleryWork, heightPx?: number) => {
    const primary = work.media[0];
    const sizeMode = {
      mode: "fixed-height" as const,
      height: heightPx ? `${heightPx}px` : isMobile ? "128px" : "192px",
    };

    if (primary && (primary.type === "image" || primary.type === "video")) {
      return (
        <MediaPreview
          src={primary.url}
          mediaType={primary.type}
          thumbnailSrc={work.image_url || undefined}
          sizeMode={sizeMode}
          hoverPlay={primary.type === "video" && !isMobile}
          enableClickPreview
          disableHoverEffect
          fileName={primary.file_name}
        />
      );
    }

    if (primary && primary.type === "pdf") {
      return (
        <MediaPreview
          src={primary.url}
          mediaType="pdf"
          thumbnailSrc={work.image_url || undefined}
          sizeMode={sizeMode}
          enableClickPreview
          disableHoverEffect
          fileName={primary.file_name}
        />
      );
    }

    if (!primary && work.youtube_link) {
      return (
        <MediaPreview
          src={work.youtube_link}
          mediaType="youtube"
          sizeMode={sizeMode}
          hoverPlay={!isMobile}
          enableClickPreview
          disableHoverEffect
        />
      );
    }

    if (!primary && work.image_url) {
      return (
        <MediaPreview
          src={work.image_url}
          mediaType="image"
          sizeMode={sizeMode}
          enableClickPreview
          disableHoverEffect
        />
      );
    }
    if (!primary) return null;

    // 音源: サムネ画像（image_url）があればそれを、なければ音そのものの実測から
    // 組み立てた生成サムネイルを表示する。
    // ホバーでは鳴らさず、クリックで画面下のフローティングプレイヤーバーを開く。
    // バー再生中は画面内のサムネイルがその再生位置に合わせて動き続ける。
    return (
      <div
        style={{ cursor: "pointer" }}
        onClick={() => setPlayingId(String(work.id))}
        role="button"
        aria-label={`${work.title} を再生`}
      >
        <MediaPreview
          src={primary.url}
          mediaType="audio"
          thumbnailSrc={work.image_url || undefined}
          sizeMode={sizeMode}
          disableHoverEffect
          fileName={primary.file_name}
          waveformUrl={primary.waveform_url || undefined}
        />
      </div>
    );
  }, [isMobile]);

  /**
   * アーティストパネル（ステートメントセクションのコンパクト版）。共通の
   * `QuoteCard` を使うので、公開プロフィールのレビューセクションと同じ意匠
   * （アバター + 引用アイコン付き本文の行 → 右下に引用線 + 表示名 + 肩書き）。
   * ステージ採寸で確定した矩形を受け、本文のクランプ行数も同じ見積もり
   * （最大 5 行）から導出する。
   * クリックで公開プロフィール `/{slug}`（RootSegmentDispatcher が
   * 公開プロフィールを表示）へ遷移する。
   */
  const renderCreatorPanel = useCallback(
    (creator: GalleryCreator, size: { width: number; height: number }) => (
      <QuoteCard
        text={creator.statement}
        name={creator.display_name}
        title={creator.title}
        avatar={{
          src: creator.avatar_url || undefined,
          name: creator.display_name,
          size: isMobile
            ? CREATOR_AVATAR_SIZE_MOBILE_PX
            : CREATOR_AVATAR_SIZE_PX,
        }}
        clampLines={estimateStatementLines(
          creator.statement,
          creatorStatementLineWidth(size.width),
        )}
        height={size.height}
        onClick={() => navigate(`/${creator.slug}`)}
        ariaLabel={`${creator.display_name} のプロフィールを見る`}
      />
    ),
    [isMobile, navigate],
  );

  // ===== ステージ投入アイテムの採寸と描画内容 =====
  // scale = 1 時点の基準サイズを確定し、描画ノードごと ScaleThroughStage へ
  // 渡す。実アスペクト比の確定（ratiosSettled）を待ってから組み立てることで
  // 飛行中の寸法ポップを防ぐ。
  const stageItems = useMemo<ScaleThroughStageItem[]>(() => {
    if (
      !search ||
      stageEntries.length === 0 ||
      layoutWidth <= 0 ||
      !ratiosSettled
    ) {
      return [];
    }
    const baseSide =
      clamp(
        Math.min(layoutWidth, viewportHeight) * BASE_SIDE_VIEWPORT_RATIO,
        BASE_SIDE_MIN_PX,
        BASE_SIDE_MAX_PX,
      ) * (isMobile ? MOBILE_BASE_SIDE_FACTOR : 1);
    // アーティストパネルの基準幅。scale = 1 時点で読みやすい幅に収める。
    const panelWidth = Math.min(
      isMobile
        ? Math.round(layoutWidth * CREATOR_PANEL_WIDTH_RATIO_MOBILE)
        : clamp(
            Math.round(layoutWidth * CREATOR_PANEL_WIDTH_RATIO),
            CREATOR_PANEL_W_MIN_PX,
            CREATOR_PANEL_W_MAX_PX,
          ),
      CREATOR_PANEL_W_MAX_PX,
    );
    return stageEntries.map<ScaleThroughStageItem>((entry) => {
      if (entry.kind === "creator") {
        const height = creatorPanelHeight(entry.creator.statement, panelWidth);
        return {
          key: stageEntryKey(entry),
          kind: "panel",
          width: panelWidth,
          height,
          node: renderCreatorPanel(entry.creator, {
            width: panelWidth,
            height,
          }),
        };
      }
      const id = String(entry.work.id);
      const ar = thumbRatios.get(id) ?? fallbackAspectRatio(entry.work);
      const { width, height } = workThumbSize(id, ar, baseSide);
      // 画像ソースを持つ作品は decode 完了までプレースホルダーを見せる
      // （音源サムネの黒背景・動画ポスター読み込み前の黒画面を出さない）。
      const waitingImage =
        workThumbImageSrc(entry.work) != null && !thumbStatus.has(id);
      return {
        key: stageEntryKey(entry),
        kind: "chip",
        width,
        // サムネ + タイトル 1 行ぶんがチップの実寸高さ（衝突判定用）。
        height: height + STAGE_TITLE_H_PX,
        node: (
          <>
            {waitingImage ? (
              <ThumbLoadingBox style={{ width, height }} />
            ) : (
              <StageThumbBox style={{ width, height }}>
                {renderThumb(entry.work, height)}
              </StageThumbBox>
            )}
            <StageTitle>{entry.work.title}</StageTitle>
          </>
        ),
      };
    });
  }, [
    search,
    stageEntries,
    layoutWidth,
    viewportHeight,
    isMobile,
    ratiosSettled,
    thumbRatios,
    thumbStatus,
    renderThumb,
    renderCreatorPanel,
  ]);

  // ヒーローを中央寄せするか（PC は常に中央、モバイルは結果表示前のみ）。
  const heroCentered = !isMobile || !search;

  return (
    <PageContainer ref={containerRef}>
      {/* ===== 検索結果: 画面固定のスケールスルー表示（ヒーローの背面） =====
        * 作品が検索 UI を避けた同心円セル配置で奥から現れ、スクロールに
        * 合わせて鮮明になりながら手前へ拡大して消える。検索条件が変わったら
        * key で仮想スクロールごと初期化する */}
      {search && !isFetchingWorks && stageItems.length > 0 && (
        <ScaleThroughStage
          key={`${search.kind}:${search.value}`}
          items={stageItems}
          exclusion={exclusionRect}
          hint={isMobile ? "スワイプ ↓" : "スクロール ↓"}
        />
      )}

      <Hero $fixedCenter={!isMobile} $centered={heroCentered}>
        <CenterCore ref={centerCoreRef}>
          <SearchField>
            <SearchIcon aria-hidden>search</SearchIcon>
            <SearchInput
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="作品名・アーティスト名・肩書き・タグで探す（例: ピアノ, 作曲家）"
              aria-label="作品名・アーティスト名・肩書き・タグで検索"
            />
          </SearchField>

          {/* タグ候補（未入力時は人気タグ）。選択で作品をステージ表示 */}
          <TagCloud>
            {tagSuggestions.map((tag) => (
              <HashTagChip
                key={tag.name}
                type="button"
                $selected={activeTag === tag.name}
                aria-pressed={activeTag === tag.name}
                onClick={() => handleSelectTag(tag.name)}
              >
                {tag.name}
              </HashTagChip>
            ))}
          </TagCloud>

          {/* 0 件のときだけ控えめに知らせる（件数そのものは表示しない） */}
          {search &&
            !isFetchingWorks &&
            worksResp &&
            stageEntries.length === 0 && (
              <span className="bodytext-13 u-text-light-gray">
                該当する作品・アーティストが見つかりませんでした
              </span>
            )}
          {search && isFetchingWorks && <SvgLoadingAnimation />}
        </CenterCore>
      </Hero>

      {/* 音源クリック時のフローティングプレイヤーバー */}
      {playingIndex >= 0 && (
        <AudioPlayerBar
          tracks={audioTracks}
          currentIndex={playingIndex}
          onIndexChange={(i) => setPlayingId(audioTracks[i].id)}
          onClose={() => setPlayingId(null)}
          openInAppreciation={openedFromShareLink.current}
        />
      )}

    </PageContainer>
  );
};

export default Gallery;
